import { cn } from "@/lib/utils";
import "@assets/css/bootstrap-grid.css";
import { Skeleton } from "@components/ui/skeleton";
import { Spinner } from "@components/ui/spinner";
import { zodResolver } from "@hookform/resolvers/zod";
import { endpoint } from "@services/api.services";
import { METHODS } from "@services/interface.service";
import { useQueryClient } from "@tanstack/react-query";
import { FormSkeleton, SkeletonTwo } from "@ui/external/components/skeleton";
import { getInputField } from "@utils/function/index.function";
import {
  isNullCheck,
  showToast,
  throwErrorAlert,
} from "@utils/helper/index.helper";
import { useHandleAction, usePathname } from "@utils/hook/index.hook";
import { FormField } from "@utils/interface/form.interface";
import {
  TemplateFormDataGiveAway,
  TemplateFormSchmeaGiveaway,
  certificateFormData,
  certificateSchema,
} from "@utils/interface/form.schema";
import { IndividualQuery } from "@utils/interface/index.interface";
import {
  CertificateData,
  StudentData,
  TemplateData,
  TemplateGiveaway,
} from "@utils/interface/journey.interface";
import { useListData, usePostData } from "@utils/query/index.query";
import { usePending, useSelectionStore } from "@utils/states/index.state";
import React, { useEffect, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { useParams } from "react-router";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Terminal } from "lucide-react";
//
const TemplateForm: React.FC = () => {
  const { searchParams, handleAction } = useHandleAction({});
  // const { type } = useParams();
  const pathname = usePathname();
  const type = pathname;
  const queryClient = useQueryClient();
  const pageItem = searchParams.get("page-item");
  const pageMode = searchParams.get("page");
  const {
    handleSubmit,
    control,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<TemplateFormDataGiveAway>({
    mode: "onChange",
    resolver: zodResolver(TemplateFormSchmeaGiveaway),
  });

  const { data: fetchData, isPending: fetchPending } = useListData<
    IndividualQuery<TemplateGiveaway>
  >({
    endpoint: endpoint["giveaway-templates"],
    key: `${pathname}-${pageItem}`,
    enabled: pageItem ? true : false,
    id: pageItem || "",
    window: false,
  });

  /* set default value */
  const { setIsRetrive }: any = usePending();
  const { setIsFormPending } = useSelectionStore();
  useEffect(() => {
    setIsRetrive(pageItem ? fetchPending : false);

    if (fetchData?.status === 200 && !fetchPending) {
      const firstResult = fetchData?.data;
      console.log(firstResult);
      if (firstResult) {
        if (pageMode == "add") {
          return reset({});
        } else {
          reset({
            name: fetchData?.data?.name || "",
            html_content: fetchData?.data?.html_content || "",
            prefix: fetchData?.data?.prefix,
          });
        }
      }
    }
  }, [fetchData, fetchPending]);

  const handleMutate = (data: any) => {
    setIsFormPending(true);
    data = {
      ...data,
    };
    if (pageItem) {
      data = {
        ...data,
        id: pageItem,
      };
    }
    return {
      data,
      endpoint: endpoint["giveaway-templates"],
      method: pageItem ? METHODS.PUT : METHODS.POST,
    };
  };
  const handleSuccess = async ({
    status,
  }: {
    data: TemplateData;
    status: number;
  }) => {
    if ([200, 201].includes(status)) {
      queryClient.invalidateQueries({ queryKey: [`${pathname}-listing`] });
      queryClient.invalidateQueries({
        queryKey: [`${pathname}-listing-scroll`],
      });
      showToast({
        description: `Template ${status === 200 ? "Updated" : "Created"}`,
        type: "success",
        title: "Success",
      });
      setIsFormPending(false);

      handleAction({
        type: "page-close",
      });
    } else {
      return new Error("Unknown API Error");
    }
  };
  const handleError = async (error: any) => {
    console.log(error, "error00");
    setIsFormPending(false);
    if (error?.data?.status == 413) {
      showToast({
        description: "File Size Too Large",
        type: "error",
        title: "Error",
      });
    }

    throwErrorAlert(error);
  };

  const { mutate } = usePostData();
  console.log("errors", errors);

  const onSubmit: SubmitHandler<TemplateFormDataGiveAway> = (data) => {
    //Add Mutation Data
    mutate(handleMutate(data), {
      onSuccess: ({ data, status }) => handleSuccess({ data, status }),
      onError: (data) => handleError({ data }),
    });
  };

  const formSchema: FormField[] = [
    {
      name: "name",
      label: "Name",
      colSpan: 6,
      type: "text",
      placeholder: "Enter Student",
      control: control,
      setValue: setValue,
      errors,
      className: `mb-[20px] min-w-full w-full`,
      visable: true,
    },
    {
      name: "prefix",
      label: "Prefix",
      colSpan: 6,
      type: "text",
      placeholder: "Enter the Prefix",
      control: control,
      setValue: setValue,
      errors,
      className: `mb-[20px] min-w-full w-full`,
      visable: true,
    },
    {
      type: "component",
      component: (
        <>
          <Alert variant="default">
            <Terminal />
            <AlertTitle>
              Heads up!{" "}
              <strong className="text-red-600">Only Use Inline CSS</strong> to
              create Certificate Templates
            </AlertTitle>
            <AlertDescription>
              You can add the certificate required input fields by right
              clicking the html editor.
            </AlertDescription>
          </Alert>
        </>
      ),
    },
    {
      name: "html_content",
      label: "HTML Editor",
      colSpan: 12,
      type: "code-mirror",
      placeholder: "Enter Certificate Template",
      control: control,
      setValue: setValue,
      errors,
      className: `mb-[20px] min-w-full w-full`,
      visable: true,
    },
  ];

  if (fetchPending && pageItem)
    return (
      <>
        <FormSkeleton />
      </>
    );

  return (
    <>
      <form
        id={type}
        onSubmit={(e) => {
          e.preventDefault();
          console.log("THE on Submit has ran");
          handleSubmit(onSubmit)();
        }}
        className="row w-full mt-4 "
      >
        {formSchema.map(
          (field, index) =>
            (field.visable || field.visable == undefined) && (
              <div
                key={index}
                className={`col-lg-${field.colSpan} col-12 lg:px-[12px] px-[8px]`}
              >
                {getInputField({ ...field })}
              </div>
            )
        )}
      </form>
    </>
  );
};

export { TemplateForm };

// const CertificateForm: React.FC = () => {
//   const { searchParams, handleAction } = useHandleAction({});
//   const { type } = useParams();
//   const pathname = usePathname();
//   const queryClient = useQueryClient();
//   const modalItem = searchParams.get("modal-item");
//   const modalMode = searchParams.get("modal");

//   const {
//     handleSubmit,
//     control,
//     reset,
//     watch,
//     setValue,
//     formState: { errors },
//   } = useForm<certificateFormData>({
//     mode: "onChange",
//     resolver: zodResolver(certificateSchema),
//   });

//   const { data: fetchData, isPending: fetchPending } = useListData<
//     IndividualQuery<CertificateData>
//   >({
//     endpoint: endpoint["certificate"],
//     key: `${pathname}-${modalItem}`,
//     enabled: modalItem ? true : false,
//     id: modalItem || "",
//     window: false,
//   });

//   /* set default value */
//   const { setIsRetrive }: any = usePending();

//   useEffect(() => {
//     setIsRetrive(modalItem ? fetchPending : false);
//     if (fetchData?.status === 200 && !fetchPending) {
//       const firstResult = fetchData?.data;
//       console.log(firstResult);
//       if (firstResult) {
//         if (modalMode == "add") {
//           return reset({});
//         } else {
//           reset(() => ({
//             student: [
//               {
//                 label: firstResult?.student?.full_name,
//                 value: isNullCheck(firstResult?.student?.id) as string,
//               },
//             ],
//             course_batch: [
//               {
//                 label: firstResult?.course_batch.name,
//                 value: firstResult?.course_batch.id,
//               },
//             ],

//             certificate_template: [
//               {
//                 label: firstResult?.certificate_template?.title,
//                 value: firstResult?.certificate_template?.id,
//               },
//             ],
//             remarks: firstResult?.remarks || "",
//             email: firstResult?.student?.email || "",
//             full_name: firstResult?.student?.full_name || "",
//             phone_number: firstResult?.student?.phone_number || "",
//             enrollment_date: firstResult?.enrollment_date || "",
//             completion_date: firstResult?.completion_date || "",
//             glimpse: firstResult?.glimpse || "",
//           }));
//         }
//       }
//     }
//   }, [fetchData?.data, fetchPending]);
//   const { setIsFormPending } = useSelectionStore();
//   const handleMutate = (
//     data: any & { course: number; batch: number; student: number }
//   ) => {
//     console.log("My Fantastic Data", data);
//     data = {
//       ...data,
//       course_batch: data["course_batch"]?.[0]["value"],
//       student: data["student"]?.[0]["value"],
//       certificate_template: data["certificate_template"]?.[0]["value"],
//     };
//     if (modalItem) {
//       data = {
//         ...data,
//         id: modalItem,
//       };
//       if (!(data.glimpse instanceof File || data.image instanceof Blob))
//         delete data.glimpse;
//     }
//     return {
//       data,
//       endpoint: endpoint["certificate"],
//       method: modalItem ? METHODS.PUT : METHODS.POST,
//     };
//   };
//   const handleSuccess = async ({
//     status,
//   }: {
//     data: CertificateData;
//     status: number;
//   }) => {
//     if ([200, 201].includes(status)) {
//       queryClient.invalidateQueries({ queryKey: [`${pathname}-listing`] });
//       queryClient.invalidateQueries({
//         queryKey: [`${pathname}-listing-scroll`],
//       });
//       showToast({
//         description: `Certificate ${status === 200 ? "Updated" : "Created"}`,
//         type: "success",
//         title: "Success",
//       });

//       handleAction({
//         type: "modal-close",
//       });
//     } else {
//       return new Error("Unknown API Error");
//     }
//   };
//   const handleError = async (error: any) => {
//     console.log(error, "error00");
//     if (error?.data?.status == 413) {
//       showToast({
//         description: "File Size Too Large",
//         type: "error",
//         title: "Error",
//       });
//     }
//     setIsFormPending(false);
//     throwErrorAlert(error);
//   };

//   const { mutate, isPending } = usePostData();
//   console.log("errors", errors);

//   const onSubmit: SubmitHandler<certificateFormData> = (data) => {
//     //Add Mutation Data
//     mutate(handleMutate(data), {
//       onSuccess: ({ data, status }) => handleSuccess({ data, status }),
//       onError: (data) => handleError({ data }),
//     });
//   };

//   const { data: selectedStudent, isPending: studentPending } = useListData<
//     IndividualQuery<StudentData>
//   >({
//     endpoint: endpoint["student"],
//     id: watch("student")?.[0]?.["value"],
//     key: "student" + watch("student")?.[0]["value"],
//     enabled: !!watch("student") && modalMode !== "edit",
//     window: false,
//   });

//   useEffect(() => {
//     if (watch("student") && modalMode !== "edit") {
//       console.log("Selected Student", selectedStudent);
//       setValue(
//         "full_name",
//         isNullCheck(selectedStudent?.data?.user?.full_name) as string
//       );
//       setValue("email", isNullCheck(selectedStudent?.data?.email) as string);
//       setValue(
//         "phone_number",
//         isNullCheck(selectedStudent?.data?.user?.phone) as string
//       );
//     }
//   }, [selectedStudent]);

//   const [isPreEdit, setIsPreEdit] = useState<
//     "certificate-remarks" | "certificate-glimpse" | null | undefined | string
//   >(null);

//   const hash = window.location.hash.substring(1);
//   useEffect(() => {
//     if (["certificate-remarks", "certificate-glimpse"]?.includes(hash)) {
//       setIsPreEdit(hash);
//     } else {
//       setIsPreEdit(null);
//     }
//   }, [hash]);
//   const formSchema: FormField[] = [
//     {
//       name: "student",
//       label: "Student",
//       colSpan: 12,
//       type: "async",
//       endpoint: endpoint["studentselect"],
//       format: { key: "full_name", value: "id" },
//       placeholder: "Enter Student",
//       control: control,
//       setValue: setValue,
//       errors,
//       className: `mb-[20px] min-w-full w-full`,
//       visable: isPreEdit
//         ? false
//         : modalMode == "edit"
//         ? false
//         : !watch("student"),
//     },
//     {
//       name: "course_batch",
//       label: "Course & Batch",
//       endpoint: watch("student")?.[0]?.value
//         ? endpoint["certificate"] + "pending-course-batches/"
//         : endpoint["course-batch"],
//       format: { key: "name", value: "id" },
//       colSpan: 12,
//       type: "async",
//       filter: watch("student")?.[0]?.value
//         ? {
//             student_id: watch("student")?.[0]?.value ?? "",
//           }
//         : {},
//       placeholder: "Select Course & Batch",
//       control: control,
//       setValue: setValue,
//       errors,
//       className: `mb-[20px] min-w-full w-full`,
//       visable: isPreEdit
//         ? false
//         : modalMode == "edit"
//         ? true
//         : !!watch("student"),
//     },

//     {
//       name: "certificate_template",
//       label: "Certificate Template",
//       colSpan: 12,
//       disabled: watch("course_batch") ? false : true,
//       type: "async",
//       placeholder: "Enter Certificate Template",
//       endpoint: endpoint["giveaway-templates"],
//       format: { key: "title", value: "id" },
//       control: control,
//       setValue: setValue,
//       filter: watch("course_batch")?.length
//         ? { course_batch__id: watch("course_batch")[0]?.["value"] }
//         : {},
//       errors,
//       className: `mb-[20px] min-w-full w-full`,
//       visable: isPreEdit
//         ? false
//         : modalMode == "edit"
//         ? true
//         : !!watch("student"),
//     },
//     {
//       type: "component",
//       colSpan: 12,
//       component: (
//         <p className="text-[18px] font-[600] mt-[20px]">Student Details</p>
//       ),
//       visable: isPreEdit
//         ? false
//         : modalMode == "edit"
//         ? true
//         : !!watch("student"),
//     },
//     {
//       name: "full_name",
//       label: "Full Name",
//       isPending: studentPending && modalMode !== "edit",
//       colSpan: 12,
//       type: "text",
//       placeholder: "Enter Fullname",
//       disabled: true,
//       control: control,
//       setValue: setValue,
//       errors,
//       className: `mb-[20px] min-w-full w-full`,
//       visable: isPreEdit
//         ? false
//         : modalMode == "edit"
//         ? true
//         : !!watch("student"),
//     },
//     {
//       name: "phone_number",
//       label: "Phone Number",
//       disabled: true,
//       isPending: studentPending && modalMode !== "edit",
//       colSpan: 6,
//       type: "tel",
//       placeholder: "Enter Phone Number",
//       control: control,
//       setValue: setValue,
//       errors,
//       className: `mb-[20px] min-w-full w-full`,
//       visable: isPreEdit
//         ? false
//         : modalMode == "edit"
//         ? true
//         : !!watch("student"),
//     },
//     {
//       name: "email",
//       label: "Email",
//       isPending: studentPending && modalMode !== "edit",
//       colSpan: 6,
//       disabled: true,
//       type: "text",
//       placeholder: "Enter Email",
//       control: control,
//       setValue: setValue,
//       errors,
//       className: `mb-[20px] min-w-full w-full`,
//       visable: isPreEdit
//         ? false
//         : modalMode == "edit"
//         ? true
//         : !!watch("student"),
//     },
//     {
//       name: "enrollment_date",
//       label: "Start Date",
//       colSpan: 6,
//       type: "date",
//       placeholder: "Enter Start Date",
//       control: control,
//       setValue: setValue,
//       errors,
//       className: `mb-[20px] min-w-full w-full`,
//       visable: isPreEdit
//         ? false
//         : modalMode == "edit"
//         ? true
//         : !!watch("student"),
//     },
//     {
//       name: "completion_date",
//       label: "End Date",
//       colSpan: 6,
//       type: "date",
//       placeholder: "Enter End Date",
//       control: control,
//       setValue: setValue,
//       errors,
//       className: `mb-[20px] min-w-full w-full`,
//       visable: isPreEdit
//         ? false
//         : modalMode == "edit"
//         ? true
//         : !!watch("student"),
//     },
//     {
//       id: "certificate-remarks",
//       name: "remarks",
//       label: "Remarks",
//       colSpan: 12,
//       type: "textarea",
//       placeholder: "Enter Description",
//       control: control,
//       setValue: setValue,
//       errors,
//       className: `mb-[20px] min-w-full !w-full`,
//       visable:
//         modalMode == "edit" && isPreEdit == "certificate-remarks"
//           ? true
//           : modalMode == "edit" && !isPreEdit
//           ? true
//           : modalMode == "edit" && isPreEdit === "certificate-glimpse"
//           ? false
//           : !!watch("student"),
//     },
//     {
//       id: "certificate-glimpse",
//       name: "glimpse",
//       label: "Glimpse",
//       colSpan: 12,
//       type: "cropper",
//       upload_type: "image",
//       control: control,
//       setValue: setValue,
//       errors,
//       className: `mb-[20px] min-w-full w-full`,
//       visable:
//         modalMode == "edit" && isPreEdit == "certificate-glimpse"
//           ? true
//           : modalMode == "edit" && !isPreEdit
//           ? true
//           : modalMode == "edit" && isPreEdit === "certificate-remarks"
//           ? false
//           : !!watch("student"),
//     },
//     {
//       type: "submit",
//       value: `${searchParams.get("modal") || ""} ${(type || pathname)?.replace(
//         "-",
//         " "
//       )}`,
//       className: "w-full h-[47px] mt-[32px] capitalize",
//       colSpan: 12,
//       variant: "default",
//       endIcon: isPending ? <Spinner /> : null,
//       disabled: isPending ? true : false,
//       visable: true,
//     },
//   ];

//   if (fetchPending && searchParams.get("modal-item"))
//     return (
//       <>
//         <SkeletonTwo />
//       </>
//     );
//   return (
//     <>
//       <form onSubmit={handleSubmit(onSubmit)} className="row w-full">
//         {formSchema.map(
//           (field, index) =>
//             (field.visable || field.visable == undefined) && (
//               <div
//                 key={index}
//                 className={`col-lg-${field.colSpan} col-12 lg:px-[12px] px-[8px]`}
//               >
//                 {field.isPending ? (
//                   <Skeleton className={cn(["h-[70px] w-full mb-[12px]"])} />
//                 ) : (
//                   getInputField({ ...field })
//                 )}
//               </div>
//             )
//         )}
//       </form>
//     </>
//   );
// };
